import type { RangeStatic } from 'quill';
import type { Op } from 'quill-delta';

import { getEmailRegex } from 'hb-react/shared/constants/emailRegex';
import { standardUrlRegex } from 'hb-react/shared/utils/validators/urlFormat';

import { getOccurrences } from './getOccurrences';

interface StylingArguments {
  regexp: RegExp;
  processUrl: (url: string) => string;
}

export const processLinkUrl = (url: string) =>
  `https://${url.replace(/https?:\/\//, '')}`;
export const processMailToUrl = (url: string) =>
  `mailto:${url.replace('mailto:', '')}`;

const stylingFactory =
  ({ regexp, processUrl }: StylingArguments) =>
  (ops: Op[]) =>
    ops.flatMap((op) => {
      if (typeof op.insert !== 'string') {
        return op;
      }

      const occurrences = getOccurrences(op.insert, regexp);

      if (occurrences.length === 0) {
        return op;
      }

      const text = op.insert as string;

      const indexes = [0, text.length];

      occurrences.forEach(({ index, length }) => {
        indexes.push(index);
        indexes.push(index + length);
      });

      const orderedIndexes = Array.from(
        new Set(indexes.toSorted((a, b) => a - b)),
      );

      const newOps = [] as Op[];

      for (let i = 0; i < orderedIndexes.length - 1; i++) {
        const start = orderedIndexes[i];
        const end = orderedIndexes[i + 1];

        const occurence = occurrences.find((o) => o.index === start);

        if (occurence) {
          newOps.push({
            ...op,
            insert: occurence.entry,
            attributes: {
              link: processUrl(occurence.entry),
            },
          });
        } else {
          const newInsert = text.slice(start, end);

          if (newInsert) {
            newOps.push({ ...op, insert: newInsert });
          }
        }
      }

      return newOps;
    });

export const addLinkStyle = stylingFactory({
  regexp: standardUrlRegex,
  processUrl: processLinkUrl,
});

export const addMailtoStyle = stylingFactory({
  regexp: getEmailRegex({ global: true, mailto: 'optional' }),
  processUrl: processMailToUrl,
});

export const bindingKeys = [' ', 'Enter'];

const getLinkBindingHandler = (
  regexp: RegExp,
  processUrl: (url: string) => string,
) =>
  function handler(this: any, range: RangeStatic, context: any) {
    const match = context.prefix.match(regexp);

    const url = match[match.length - 1];

    const [, after] = context.prefix.split(url);

    const ops = [] as Op[];

    const retainLength = range.index - url.length - after.length;

    // `retain: 0` throws an error in quill
    if (retainLength > 0) {
      ops.push({ retain: retainLength });
    }
    ops.push({ delete: url.length });
    ops.push({
      insert: url,
      attributes: { link: processUrl(url) },
    });

    this.quill.updateContents({ ops });

    return true;
  };

const createLinksBindingFactory = ({ name, keys, regexp, processUrl }) =>
  keys.reduce((acc, key, index) => {
    acc[`${name}${index}`] = {
      collapsed: true,
      key,
      prefix: regexp,
      handler: getLinkBindingHandler(regexp, processUrl),
    };

    return acc;
  }, {});

export const mailtoBindings = createLinksBindingFactory({
  name: 'mailToBinding',
  keys: bindingKeys,
  regexp: getEmailRegex({ global: true, mailto: 'optional' }),
  processUrl: processMailToUrl,
});

export const linkBindings = createLinksBindingFactory({
  name: 'linkBinding',
  keys: bindingKeys,
  regexp: standardUrlRegex,
  processUrl: processLinkUrl,
});
